import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Search, Edit, Delete } from "@mui/icons-material";
import { useFaqs } from "../hooks/useFaqs";
import { apiService } from "../api/services/apiService";
import { PaginationControls } from "../components/common/PaginationControls";
import { useNavigate } from "react-router-dom";
import type { FAQ, EntityStatus } from "../types";

const ITEMS_PER_PAGE = 10;

const ManageFaqs: React.FC = () => {
  const navigate = useNavigate();
  const { faqs: allFaqs, loading, error } = useFaqs();

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // delete
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // edit
  const [editFaq, setEditFaq] = useState<FAQ | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    setFaqs(allFaqs);
  }, [allFaqs]);

  /* ---------------- Delete ---------------- */

  const handleDeleteClick = (uuid: string) => {
    setDeleteUuid(uuid);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUuid) return;
    setIsDeleting(true);
    try {
      const success = await apiService.deleteFaq(deleteUuid);
      if (success) {
        setFaqs((prev) => prev.filter((f) => f.uuid !== deleteUuid));
      }
    } catch (err: any) {
      alert(err.message || "Failed to delete FAQ");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
      setDeleteUuid(null);
    }
  };

  /* ---------------- Edit ---------------- */

  const handleSaveEdit = async () => {
    if (!editFaq) return;
    setIsSaving(true);
    try {
      const updated = await apiService.updateFaq(editFaq.uuid, {
        name: editFaq.name,
        description: editFaq.description ?? undefined,
        question: editFaq.question ?? undefined,
        answer: editFaq.answer ?? undefined,
        status: editFaq.status,
      });

      setFaqs((prev) =>
        prev.map((f) => (f.uuid === updated.uuid ? updated : f)),
      );
      setEditFaq(null);
      setSuccessOpen(true);
    } catch (err: any) {
      alert(err.message || "Failed to update FAQ");
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------- Filtering & Pagination ---------------- */

  const filteredFaqs = faqs.filter((faq) =>
    faq.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredFaqs.length / ITEMS_PER_PAGE);
  const paginatedFaqs = filteredFaqs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          FAQs
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/faqs/add")}
          >
            Add FAQ
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedFaqs.map((faq) => (
                  <TableRow key={faq.uuid} hover>
                    <TableCell>{faq.name}</TableCell>
                    <TableCell>{faq.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={faq.status}
                        size="small"
                        color={faq.status === "ACTIVE" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setEditFaq(faq)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(faq.uuid)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={!!editFaq}
        onClose={() => setEditFaq(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit FAQ</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={editFaq?.name || ""}
            onChange={(e) =>
              setEditFaq((prev) =>
                prev ? { ...prev, name: e.target.value } : prev,
              )
            }
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={editFaq?.description || ""}
            onChange={(e) =>
              setEditFaq((prev) =>
                prev ? { ...prev, description: e.target.value } : prev,
              )
            }
          />

          <TextField
            select
            fullWidth
            label="Status"
            margin="normal"
            value={editFaq?.status || "INACTIVE"}
            onChange={(e) =>
              setEditFaq((prev) =>
                prev
                  ? { ...prev, status: e.target.value as EntityStatus }
                  : prev,
              )
            }
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Question"
            margin="normal"
            value={editFaq?.question || ""}
            onChange={(e) =>
              setEditFaq((prev) =>
                prev ? { ...prev, question: e.target.value } : prev,
              )
            }
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Answer"
            margin="normal"
            value={editFaq?.answer || ""}
            onChange={(e) =>
              setEditFaq((prev) =>
                prev ? { ...prev, answer: e.target.value } : prev,
              )
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditFaq(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={isSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete FAQ?</strong>
        </DialogTitle>
        <DialogContent>Are you sure you want to delete this FAQ?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={isDeleting}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success">FAQ updated successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageFaqs;
